import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { X, AlertTriangle, Terminal, Activity, RefreshCw, Users, Server, KeyRound, DollarSign, Bell, Mail, Send, FileText, MessageCircle, Info, Smartphone, Globe, Circle, Copy, Database, Trash2, HandCoins } from 'lucide-react';
import api from '../api';
import Pricing from './Pricing';
import { can } from '../auth';

// Driver APK, served from our own server.
//
// This used to point straight at the Expo build artifact, but those expire
// after 14 days — drivers sent a login message a few weeks later got
// "NoSuchKey" instead of a download. Copy each new build here instead:
//   scp the .apk to  /var/www/expressway-courier/downloads/expressway-driver.apk
// The link then never changes and never dies.
const APP_APK_URL = 'https://expressway.xcourier.co.za/downloads/expressway-driver.apk';

// Readable password (no ambiguous 0/O/1/l characters)
function genPassword() {
  const a = 'abcdefghjkmnpqrstuvwxyz23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
  return Array.from({ length: 8 }, () => a[Math.floor(Math.random() * a.length)]).join('');
}

// SA local (0xx) → WhatsApp international (27xx)
function waNumber(phone) {
  if (!phone) return null;
  const d = String(phone).replace(/\D/g, '');
  if (d.startsWith('27')) return d;
  if (d.startsWith('0')) return '27' + d.slice(1);
  return d ? '27' + d : null;
}

// Set / reveal / WhatsApp a user's app login
function CredentialsModal({ user: u, onClose }) {
  const [password, setPassword] = useState(genPassword());
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  const save = async () => {
    if (password.trim().length < 4) return toast.error('Password must be at least 4 characters');
    setLoading(true);
    try {
      await api.post(`/users/${u.id}/set-password`, { password });
      setSaved(true);
      toast.success('Password set');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard?.writeText(`Username: ${u.email}\nPassword: ${password}`);
    toast.success('Copied');
  };

  const wa = waNumber(u.phone);
  const waText = encodeURIComponent(
    `Hi ${u.name}, here are your Expressway Driver app login details:\n\n` +
    `Username: ${u.email}\nPassword: ${password}\n\n` +
    `Download the app: ${APP_APK_URL}\n\n` +
    `Install it, open the app and log in. Please keep these safe.`
  );
  const waUrl = wa ? `https://wa.me/${wa}?text=${waText}` : null;

  return (
    <div className="fixed inset-0 z-50 flex items-start p-4 bg-black/50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 m-auto my-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-gray-800">App login — {u.name}</h2>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Username (email)</label>
            <input type="text" readOnly value={u.email}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-700" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Password</label>
            <div className="flex gap-2">
              {/* type=text on purpose — admin must see the real characters */}
              <input type="text" value={password} onChange={(e) => { setPassword(e.target.value); setSaved(false); }}
                className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-400" />
              <button type="button" onClick={() => { setPassword(genPassword()); setSaved(false); }}
                title="Generate new" className="px-3 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
                <RefreshCw size={15} />
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">Type your own or generate one. Saving replaces this user's current password.</p>
          </div>

          {!saved ? (
            <button onClick={save} disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              {loading ? 'Saving…' : 'Save password'}
            </button>
          ) : (
            <div className="border border-green-200 bg-green-50 rounded-lg p-3 space-y-3">
              <p className="text-sm font-semibold text-green-700">✅ Password set — send it to the driver</p>
              <div className="bg-white rounded-lg border border-green-100 p-3 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Username</span><span className="font-mono text-gray-800">{u.email}</span></div>
                <div className="flex justify-between mt-1"><span className="text-gray-500">Password</span><span className="font-mono font-bold text-gray-900">{password}</span></div>
              </div>
              <div className="flex gap-2">
                <button onClick={copy}
                  className="flex items-center justify-center gap-1.5 flex-1 border border-gray-200 text-gray-700 hover:bg-gray-50 py-2 rounded-lg text-sm font-medium">
                  <Copy size={15} /> Copy
                </button>
                {waUrl ? (
                  <a href={waUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1.5 flex-1 bg-[#25D366] hover:bg-[#20b558] text-white py-2 rounded-lg text-sm font-medium">
                    💬 WhatsApp to driver
                  </a>
                ) : (
                  <span className="flex-1 text-center text-xs text-gray-400 py-2">No phone number on file</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Roles: who may do what, built from tick boxes ─────────────────────────────
// A role is a saved set of permissions. Admin is fixed; the other built-in
// roles can be re-ticked but not deleted; the office can add its own. On top
// of a role, a single user can be given extra ticks in the user form.

function usePermissionCatalogue() {
  return useQuery({
    queryKey: ['permission-catalogue'],
    queryFn: () => api.get('/auth/permissions').then(r => r.data),
    staleTime: 10 * 60 * 1000,
  });
}
function useRoles() {
  return useQuery({ queryKey: ['roles'], queryFn: () => api.get('/roles').then(r => r.data) });
}

// Grouped tick boxes. `locked` = ticks that come from the role and cannot be
// unticked here (used for a user's extras); `disabled` = read-only.
function PermissionPicker({ catalogue = [], value = [], onChange, locked = [], disabled = false }) {
  const groups = [...new Set(catalogue.map(p => p.group))];
  const has = (k) => value.includes(k) || locked.includes(k);
  const toggle = (k) => {
    if (disabled || locked.includes(k)) return;
    onChange(value.includes(k) ? value.filter(x => x !== k) : [...value, k]);
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {groups.map((g) => (
        <div key={g} className="border rounded-lg p-3">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">{g}</p>
          <div className="space-y-1.5">
            {catalogue.filter(p => p.group === g).map((p) => {
              const isLocked = locked.includes(p.key);
              return (
                <label key={p.key} className={`flex items-start gap-2 ${disabled || isLocked ? 'opacity-70' : 'cursor-pointer'}`}>
                  <input type="checkbox" checked={has(p.key)} onChange={() => toggle(p.key)}
                    disabled={disabled || isLocked} className="mt-0.5 accent-orange-500" />
                  <span>
                    <span className="text-sm text-gray-800">{p.label}</span>
                    {isLocked && <span className="ml-1 text-[10px] text-gray-400">(from role)</span>}
                    <span className="block text-[11px] text-gray-400 leading-snug">{p.description}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function RoleForm({ role, catalogue, onClose, onSaved }) {
  const isEdit = !!role;
  const isAdminRole = role?.key === 'admin';
  const [form, setForm] = useState({
    name: role?.name || '', description: role?.description || '', permissions: role?.permissions || [],
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name.trim()) return toast.error('Give the role a name');
    setSaving(true);
    try {
      if (isEdit) await api.put(`/roles/${role.id}`, form);
      else await api.post('/roles', form);
      toast.success(isEdit ? 'Role saved — everyone with this role changes within a minute' : 'Role created');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the role');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start p-4 bg-black/50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-6 m-auto my-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-gray-800">{isEdit ? `Edit role: ${role.name}` : 'New role'}</h2>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Role name</label>
            <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Bookkeeper" className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description (shown when picking a role)</label>
            <input value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
        </div>
        {isAdminRole ? (
          <p className="text-sm text-gray-500 bg-gray-50 border rounded-lg p-3 mb-4">Admin always has every permission. Only its name and description can change.</p>
        ) : (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-gray-600">Tick what this role may do</p>
              <span className="text-xs text-gray-400">{form.permissions.length} of {catalogue.length} ticked</span>
            </div>
            <PermissionPicker catalogue={catalogue} value={form.permissions}
              onChange={(permissions) => setForm(f => ({ ...f, permissions }))} />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border text-gray-600">Cancel</button>
          <button onClick={save} disabled={saving}
            className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
            {saving ? 'Saving…' : isEdit ? 'Save role' : 'Create role'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TabRoles() {
  const qc = useQueryClient();
  const { data: roles = [], isLoading } = useRoles();
  const { data: catalogue = [] } = usePermissionCatalogue();
  const [editing, setEditing] = useState(null);   // null | 'new' | role
  const refresh = () => { qc.invalidateQueries({ queryKey: ['roles'] }); qc.invalidateQueries({ queryKey: ['users'] }); setEditing(null); };
  const remove = async (r) => {
    if (!window.confirm(`Delete the role "${r.name}"?`)) return;
    try { await api.delete(`/roles/${r.id}`); toast.success('Role deleted'); refresh(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not delete the role'); }
  };
  const labelOf = (k) => (catalogue.find(p => p.key === k) || { label: k }).label;

  return (
    <div className="space-y-4">
      {editing && (
        <RoleForm role={editing === 'new' ? null : editing} catalogue={catalogue} onClose={() => setEditing(null)} onSaved={refresh} />
      )}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="font-semibold text-gray-800">Roles</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              A role is a saved set of tick boxes. Give each user a role, then tick any extras that one person needs
              on top. Changing a role changes everyone who has it. Admin is fixed; the other built-in roles can be
              re-ticked but not deleted.
            </p>
          </div>
          <button onClick={() => setEditing('new')}
            className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap">
            <Users size={15} /> New role
          </button>
        </div>
        {isLoading ? <p className="text-sm text-gray-400">Loading…</p> : (
          <div className="space-y-3">
            {roles.map((r) => (
              <div key={r.id} className="border rounded-lg p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800">
                      {r.name}
                      {r.is_system && <span className="ml-2 text-[10px] uppercase bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">built in</span>}
                      <span className="ml-2 text-xs text-gray-400">{r.user_count} user{r.user_count === 1 ? '' : 's'}</span>
                    </p>
                    {r.description && <p className="text-xs text-gray-500 mt-0.5">{r.description}</p>}
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <button onClick={() => setEditing(r)} className="text-orange-600 hover:underline">Edit</button>
                    {!r.is_system && <button onClick={() => remove(r)} className="text-gray-400 hover:text-red-500 hover:underline">Delete</button>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {r.key === 'admin'
                    ? <span className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full">everything</span>
                    : (r.permissions || []).map((k) => (
                      <span key={k} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{labelOf(k)}</span>
                    ))}
                  {r.key !== 'admin' && (r.permissions || []).length === 0 && <span className="text-xs text-gray-400">nothing ticked yet</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UserModal({ existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const { data: roles = [] } = useRoles();
  const { data: catalogue = [] } = usePermissionCatalogue();
  const [form, setForm] = useState(
    isEdit
      ? { name: existing.name, email: existing.email, phone: existing.phone || '', role_id: existing.role_id || '',
          extra_permissions: existing.extra_permissions || [], active: existing.active, password: '',
          is_driver: !!existing.is_driver }
      : { name: '', email: '', password: '', role_id: '', phone: '', extra_permissions: [], is_driver: false }
  );
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Default a new user to the Driver role once roles arrive
  React.useEffect(() => {
    if (!isEdit && !form.role_id && roles.length) {
      const d = roles.find(r => r.key === 'driver') || roles[0];
      setForm(f => ({ ...f, role_id: d.id }));
    }
  }, [roles, isEdit, form.role_id]);

  const role = roles.find(r => r.id === form.role_id);
  const rolePerms = role?.key === 'admin' ? catalogue.map(p => p.key) : (role?.permissions || []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error('Name and email required');
    if (!form.role_id) return toast.error('Pick a role');
    if (!isEdit && !form.password) return toast.error('Password required for new users');
    setLoading(true);
    try {
      // Extras that the role already grants are dropped — they'd be noise
      const body = { ...form, extra_permissions: form.extra_permissions.filter(k => !rolePerms.includes(k)) };
      if (isEdit) { await api.patch(`/users/${existing.id}`, body); toast.success('User updated'); }
      else { await api.post('/users', body); toast.success('User created'); }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save user'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start p-4 bg-black/50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 m-auto my-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-gray-800">{isEdit ? 'Edit User' : 'Create User'}</h2>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Role</label>
            <div className="space-y-2">
              {roles.map((r) => (
                <label key={r.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${form.role_id === r.id ? 'bg-orange-50 border-orange-300 border-2' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" name="role_id" value={r.id} checked={form.role_id === r.id} onChange={set('role_id')} className="mt-0.5 accent-orange-500" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{r.name}</p>
                    {r.description && <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{r.description}</p>}
                  </div>
                </label>
              ))}
            </div>
          </div>

          <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${form.is_driver || role?.key === 'driver' ? 'bg-sky-50 border-sky-300' : 'border-gray-200'}`}>
            <input type="checkbox" checked={!!form.is_driver || role?.key === 'driver'} disabled={role?.key === 'driver'}
              onChange={(e) => setForm((f) => ({ ...f, is_driver: e.target.checked }))} className="mt-0.5 accent-sky-600" />
            <div>
              <p className="text-sm font-semibold text-gray-800">Works as a driver</p>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                Appears in the driver lists (assign, runsheets, driver reports) and can use the driver app — GPS during
                working hours, SOS, weight entry — on top of whatever the role allows. Office staff who also collect or deliver.
              </p>
            </div>
          </label>

          {role && role.key !== 'admin' && (
            <details className="border rounded-lg p-3" open={form.extra_permissions.length > 0}>
              <summary className="text-xs font-medium text-gray-600 cursor-pointer">
                Extra features for this user only
                {form.extra_permissions.filter(k => !rolePerms.includes(k)).length > 0 && (
                  <span className="ml-2 text-orange-600">+{form.extra_permissions.filter(k => !rolePerms.includes(k)).length}</span>
                )}
              </summary>
              <p className="text-[11px] text-gray-400 mt-1 mb-2">Greyed ticks come from the role. Tick anything more this person needs.</p>
              <PermissionPicker catalogue={catalogue} value={form.extra_permissions} locked={rolePerms}
                onChange={(extra_permissions) => setForm(f => ({ ...f, extra_permissions }))} />
            </details>
          )}

          <div className="pt-1 border-t space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">User Details</p>
            {[['name','Full Name *','text'],['email','Email *','email'],['phone','Phone','text']].map(([k,lbl,type]) => (
              <div key={k}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{lbl}</label>
                <input type={type} value={form[k]} onChange={set(k)}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
            ))}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {isEdit ? 'New Password (leave blank to keep current)' : 'Password *'}
              </label>
              {/* autoComplete="new-password" is essential: without it Chrome
                  autofills the signed-in admin's own saved password into this
                  box when the modal opens, and saving then silently replaces
                  the other user's password with the admin's. */}
              <input type="password" value={form.password} onChange={set('password')}
                autoComplete="new-password"
                placeholder={isEdit ? 'Leave blank to keep unchanged' : ''}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              {isEdit && (
                <p className="text-xs text-gray-400 mt-1">
                  To see the password you are setting in plain text, use <strong>App login</strong> on the user
                  row instead — it shows the exact characters so you can read them out or send them.
                </p>
              )}
            </div>
            {isEdit && (
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input type="checkbox" checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  className="accent-orange-500" />
                <span className="text-gray-700">Account active</span>
              </label>
            )}
          </div>
          <button type="submit" disabled={loading}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50">
            {loading ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create User')}
          </button>
        </form>
      </div>
    </div>
  );
}

const ROLE_COLORS = { admin: 'bg-red-100 text-red-700', depot: 'bg-purple-100 text-purple-700', driver: 'bg-blue-100 text-blue-700' };

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)  return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function OnlineUsers() {
  const { data: users = [], refetch, isLoading } = useQuery({
    queryKey: ['users-online'],
    queryFn: () => api.get('/users/online').then(r => r.data),
    refetchInterval: 30000, // auto-refresh every 30 s
  });

  const getStatus = (lastSeen) => {
    if (!lastSeen) return 'never';
    const mins = (Date.now() - new Date(lastSeen)) / 60000;
    if (mins < 5)  return 'online';
    if (mins < 60) return 'recent';
    return 'offline';
  };

  const statusDot = { online: 'bg-green-500', recent: 'bg-yellow-400', offline: 'bg-gray-300', never: 'bg-gray-200' };
  const statusLabel = { online: 'Online now', recent: 'Recently active', offline: 'Offline', never: 'Never logged in' };

  const online = users.filter(u => getStatus(u.last_seen_at) === 'online');
  const recent = users.filter(u => getStatus(u.last_seen_at) === 'recent');
  const offline = users.filter(u => ['offline','never'].includes(getStatus(u.last_seen_at)));

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-semibold text-gray-800 flex items-center gap-2">
            <Circle size={10} className="fill-green-500 text-green-500" />
            Connected Users
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Live activity — green = active in last 5 min · yellow = last hour
          </p>
        </div>
        <button onClick={() => refetch()} className="text-gray-400 hover:text-orange-500 transition-colors" title="Refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-400 text-center py-6">Loading…</div>
      ) : (
        <div className="space-y-1">
          {[...online, ...recent, ...offline].map(u => {
            const status = getStatus(u.last_seen_at);
            const ago    = timeAgo(u.last_seen_at);
            return (
              <div key={u.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                {/* Status dot */}
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${statusDot[status]} ${status === 'online' ? 'ring-2 ring-green-200' : ''}`} />

                {/* Name + role */}
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-gray-800">{u.name}</span>
                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full font-medium ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-600'}`}>{u.role_name || u.role}</span>{u.is_driver && u.role !== 'driver' && <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full font-medium bg-sky-100 text-sky-700" title="Works as a driver">driver</span>}
                </div>

                {/* Platform icon */}
                {u.last_platform && (
                  <div className="flex items-center gap-1 text-gray-400" title={u.last_platform === 'web' ? 'Web browser' : 'Android app'}>
                    {u.last_platform === 'web'
                      ? <Globe size={13} />
                      : <Smartphone size={13} />
                    }
                    <span className="text-xs hidden sm:inline">{u.last_platform}</span>
                  </div>
                )}

                {/* Last seen */}
                <div className="text-right flex-shrink-0">
                  {ago ? (
                    <span className={`text-xs font-medium ${status === 'online' ? 'text-green-600' : status === 'recent' ? 'text-yellow-600' : 'text-gray-400'}`}>
                      {ago}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300">never</span>
                  )}
                </div>
              </div>
            );
          })}
          {users.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6">No users found</p>
          )}
        </div>
      )}

      <div className="mt-3 pt-3 border-t flex gap-4 text-xs text-gray-400">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Online now ({online.length})</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" /> Last hour ({recent.length})</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-gray-300 inline-block" /> Offline ({offline.length})</span>
      </div>
    </div>
  );
}

function TabUsers() {
  const qc = useQueryClient();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [userModal, setUserModal] = useState(null);
  const [credUser, setCredUser] = useState(null);

  const { data: users = [] } = useQuery({
    queryKey: ['all-users'],
    queryFn: () => api.get('/users').then((r) => r.data),
  });

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { ...u, active: !u.active });
      toast.success(u.active ? 'User deactivated' : 'User activated');
      qc.invalidateQueries({ queryKey: ['all-users'] });
    } catch { toast.error('Failed to update user'); }
  };

  return (
    <div className="space-y-4">
      <OnlineUsers />

      {userModal && (
        <UserModal
          existing={userModal === 'add' ? null : userModal}
          onClose={() => setUserModal(null)}
          onSaved={() => { setUserModal(null); qc.invalidateQueries({ queryKey: ['all-users'] }); }}
        />
      )}
      {credUser && (
        <CredentialsModal user={credUser} onClose={() => setCredUser(null)} />
      )}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold text-gray-800">Users</h2>
            <p className="text-xs text-gray-400 mt-0.5">Manage staff accounts and roles</p>
          </div>
          <button onClick={() => setUserModal('add')}
            className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium px-4 py-2 rounded-lg">
            + Add User
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Role</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-800">{u.name}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs">{u.email}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-600'}`}>{u.role_name || u.role}</span>{u.is_driver && u.role !== 'driver' && <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full font-medium bg-sky-100 text-sky-700" title="Works as a driver">driver</span>}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-xs px-2 py-1 rounded-full ${u.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {u.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => setCredUser(u)} className="inline-flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-700">
                        <KeyRound size={13} /> Set &amp; send
                      </button>
                      <button onClick={() => setUserModal(u)} className="text-xs text-orange-500 hover:underline">Edit</button>
                      {u.id !== user.id && (
                        <button onClick={() => toggleActive(u)} className="text-xs text-gray-400 hover:text-red-500 hover:underline">
                          {u.active ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// The local Overberg address data behind the address suggestions and pins
function AddressDataCard() {
  const { data, refetch } = useQuery({
    queryKey: ['geo-status'],
    queryFn: () => api.get('/geo/status').then((r) => r.data),
    refetchInterval: 60000,
  });
  const [busy, setBusy] = useState(false);
  const reload = async () => {
    if (!window.confirm('Fetch a fresh copy of the provincial address data? It takes a few minutes and runs in the background.')) return;
    setBusy(true);
    try { await api.post('/geo/reload'); toast.success('Refresh started — check back in a few minutes'); setTimeout(() => refetch(), 60000); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not start the refresh'); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Globe size={16} className="text-orange-500" />
          <h2 className="font-semibold text-gray-700">Address data (Overberg)</h2>
        </div>
        <button onClick={reload} disabled={busy}
          className="text-xs border rounded-lg px-3 py-1.5 text-gray-600 hover:border-orange-400 disabled:opacity-50">
          {busy ? 'Starting…' : 'Refresh now'}
        </button>
      </div>
      <p className="text-xs text-gray-400 mb-3">
        Western Cape provincial address points used for address suggestions and map pins in the Overberg towns,
        so most lookups never touch Google. Refreshed automatically once a month.
      </p>
      {data ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div className="bg-gray-50 rounded-lg p-3"><p className="text-lg font-bold text-gray-800">{data.points.toLocaleString()}</p><p className="text-xs text-gray-500">address points · {data.towns} towns</p></div>
          <div className="bg-gray-50 rounded-lg p-3"><p className="text-lg font-bold text-gray-800">{data.polygons}</p><p className="text-xs text-gray-500">suburb boundaries</p></div>
          <div className="bg-gray-50 rounded-lg p-3"><p className="text-lg font-bold text-gray-800">{data.pinned} / {data.clients}</p><p className="text-xs text-gray-500">clients with a map pin</p></div>
          <div className="bg-gray-50 rounded-lg p-3"><p className="text-sm font-bold text-gray-800">{data.loaded_at ? new Date(data.loaded_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : 'not loaded yet'}</p><p className="text-xs text-gray-500">last refresh{data.google_server_key ? ' · Google key set' : ' · no Google server key'}</p></div>
        </div>
      ) : <p className="text-sm text-gray-400">Loading…</p>}
      {data && data.unpinned_with_address > 0 && (
        <p className="text-xs text-gray-500 mt-3">
          {data.unpinned_with_address} client{data.unpinned_with_address === 1 ? ' has' : 's have'} an address but no pin —
          use <strong>Pin from the local address data</strong> on the Missing Addresses page.
        </p>
      )}
    </div>
  );
}

function TabServer() {
  const [maintLoading, setMaintLoading] = useState(false);
  const [restartLoading, setRestartLoading] = useState(false);

  const { data: maintStatus, refetch: refetchMaint } = useQuery({
    queryKey: ['maintenance-status'],
    queryFn: () => api.get('/settings/maintenance').then((r) => r.data),
  });

  const { data: apiStatus, refetch: refetchApi, isLoading: apiLoading } = useQuery({
    queryKey: ['api-status'],
    queryFn: () => api.get('/settings/api-status').then((r) => r.data),
    refetchInterval: 30000,
  });

  const restartApi = async () => {
    if (!window.confirm('Restart the API server? The site will be unavailable for a few seconds.')) return;
    setRestartLoading(true);
    try {
      await api.post('/settings/api-restart');
      toast.success('API restarted successfully');
      setTimeout(() => refetchApi(), 3000);
    } catch { toast.error('Failed to restart API'); }
    finally { setRestartLoading(false); }
  };

  const toggleMaintenance = async (on) => {
    setMaintLoading(true);
    try {
      await api.post('/settings/maintenance', { active: on });
      await refetchMaint();
      toast.success(on ? 'Maintenance mode ON — site shows upgrade page' : 'Maintenance mode OFF — site is live');
    } catch { toast.error('Failed to toggle maintenance mode'); }
    finally { setMaintLoading(false); }
  };

  return (
    <div className="space-y-5">
      <AddressDataCard />
      {/* API Status */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Activity size={16} className="text-orange-500" />
            <h2 className="font-semibold text-gray-700">API Server Status</h2>
          </div>
          <button onClick={() => refetchApi()} className="text-gray-400 hover:text-orange-500 transition-colors" title="Refresh">
            <RefreshCw size={14} />
          </button>
        </div>
        <p className="text-xs text-gray-400 mb-5">Live status of the backend API process running on the server.</p>

        {apiLoading ? (
          <div className="text-sm text-gray-400 py-4 text-center">Checking API status...</div>
        ) : !apiStatus?.found ? (
          <div className="flex items-center gap-3 p-4 bg-red-50 border-2 border-red-200 rounded-xl mb-5">
            <div className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-700">API process not found</p>
              <p className="text-xs text-red-500 mt-0.5">PM2 cannot find the expressway-api process</p>
            </div>
          </div>
        ) : (
          <div className={`flex items-center justify-between p-4 rounded-xl border-2 mb-5 transition-colors ${apiStatus.status === 'online' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full flex-shrink-0 ${apiStatus.status === 'online' ? 'bg-green-500' : 'bg-red-500'}`} />
              <div>
                <p className="text-sm font-semibold text-gray-800 capitalize">{apiStatus.status === 'online' ? 'Online' : apiStatus.status}</p>
                <div className="flex gap-3 mt-0.5 flex-wrap">
                  {apiStatus.uptime && <p className="text-xs text-gray-500">Up since {new Date(apiStatus.uptime).toLocaleString('en-ZA')}</p>}
                  {apiStatus.memory != null && <p className="text-xs text-gray-500">Memory: {(apiStatus.memory / 1024 / 1024).toFixed(1)} MB</p>}
                  {apiStatus.restart_count != null && <p className="text-xs text-gray-500">Restarts: {apiStatus.restart_count}</p>}
                </div>
              </div>
            </div>
            <button onClick={restartApi} disabled={restartLoading}
              className="flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
              <RefreshCw size={13} className={restartLoading ? 'animate-spin' : ''} />
              {restartLoading ? 'Restarting...' : 'Restart'}
            </button>
          </div>
        )}

        <div className="border rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 bg-gray-900 px-4 py-2.5">
            <Terminal size={13} className="text-gray-400" />
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">SSH Commands</p>
          </div>
          <div className="bg-gray-950 p-4 space-y-3">
            {[
              { label: 'Check status:', cmd: 'ssh expressway-courier "pm2 list"' },
              { label: 'Restart API:', cmd: 'ssh expressway-courier "pm2 restart expressway-api"' },
              { label: 'View logs:', cmd: 'ssh expressway-courier "pm2 logs expressway-api --lines 50"' },
              { label: 'Rebuild frontend:', cmd: 'ssh expressway-courier "cd /var/www/expressway-courier/web && npm run build"' },
            ].map(({ label, cmd }) => (
              <div key={label}>
                <p className="text-xs text-gray-500 mb-1">{label}</p>
                <code className="block text-xs text-green-400 font-mono bg-gray-900 px-3 py-2 rounded-lg select-all break-all">{cmd}</code>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Maintenance Mode */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle size={16} className="text-orange-500" />
          <h2 className="font-semibold text-gray-700">Maintenance Mode</h2>
        </div>
        <p className="text-xs text-gray-400 mb-5">
          When turned on, all visitors see the "We'll be back shortly" page. Use before doing upgrades.
        </p>

        <div className="flex items-center justify-between p-4 rounded-xl border-2 mb-5 transition-colors"
          style={{ borderColor: maintStatus?.active ? '#f97316' : '#e5e7eb', background: maintStatus?.active ? '#fff7ed' : '#f9fafb' }}>
          <div>
            <p className="text-sm font-semibold text-gray-800">
              Status: <span className={maintStatus?.active ? 'text-orange-500' : 'text-green-600'}>
                {maintStatus?.active ? 'MAINTENANCE ON' : 'SITE LIVE'}
              </span>
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {maintStatus?.active ? 'Clients currently see the maintenance page' : 'Website is running normally'}
            </p>
          </div>
          {maintStatus?.active ? (
            <button onClick={() => toggleMaintenance(false)} disabled={maintLoading}
              className="bg-green-500 hover:bg-green-600 text-white text-sm font-semibold px-5 py-2 rounded-lg disabled:opacity-50">
              {maintLoading ? 'Turning off...' : 'Turn OFF'}
            </button>
          ) : (
            <button onClick={() => toggleMaintenance(true)} disabled={maintLoading}
              className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-5 py-2 rounded-lg disabled:opacity-50">
              {maintLoading ? 'Turning on...' : 'Turn ON'}
            </button>
          )}
        </div>

        <div className="border rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 bg-gray-900 px-4 py-2.5">
            <Terminal size={13} className="text-gray-400" />
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">SSH Commands</p>
          </div>
          <div className="bg-gray-950 p-4 space-y-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">Turn maintenance ON:</p>
              <code className="block text-xs text-green-400 font-mono bg-gray-900 px-3 py-2 rounded-lg select-all">
                ssh expressway-courier "touch /var/www/maintenance_on"
              </code>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Turn maintenance OFF:</p>
              <code className="block text-xs text-green-400 font-mono bg-gray-900 px-3 py-2 rounded-lg select-all">
                ssh expressway-courier "rm /var/www/maintenance_on"
              </code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabAccount() {
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) return toast.error('New passwords do not match');
    if (pw.next.length < 6) return toast.error('Password must be at least 6 characters');
    setPwLoading(true);
    try {
      await api.post('/auth/change-password', { currentPassword: pw.current, newPassword: pw.next });
      toast.success('Password changed successfully');
      setPw({ current: '', next: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to change password'); }
    finally { setPwLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="font-semibold text-gray-700 mb-4">Change Your Password</h2>
        <form onSubmit={changePassword} className="space-y-3 max-w-sm">
          {[['current','Current Password'],['next','New Password'],['confirm','Confirm New Password']].map(([k,lbl]) => (
            <div key={k}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{lbl}</label>
              <input type="password" value={pw[k]} onChange={(e) => setPw((p) => ({ ...p, [k]: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" required />
            </div>
          ))}
          <button type="submit" disabled={pwLoading}
            className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
            {pwLoading ? 'Saving...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Driver safety: who gets the SOS alerts, what number the app dials, and the
// hours the location trail records. Couriers keep odd hours, so the window is
// wide by default and fully editable here — the app picks changes up on its own.
function SafetyCard() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/settings/safety').then(r => setForm(r.data)).catch(() => setForm({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings/safety', form);
      toast.success('Driver safety settings saved — the app updates itself next time it opens');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <h2 className="font-semibold text-gray-800 flex items-center gap-2">🆘 Driver safety — SOS &amp; tracking hours</h2>
      <p className="text-xs text-gray-400 mt-0.5 mb-4">
        When a driver presses SOS, the office gets a Telegram message (set up below) and an email, and the
        driver&apos;s phone dials the SOS number. Location tracking only records between the hours set here.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">SOS phone — the number the driver&apos;s phone dials</label>
          <input type="tel" value={form.sos_phone || ''} onChange={set('sos_phone')} placeholder="e.g. 0686129215"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">SOS alert email</label>
          <input type="email" value={form.sos_email || ''} onChange={set('sos_email')}
            placeholder={form.fallback_email ? `blank = ${form.fallback_email}` : 'e.g. you@example.com'}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tracking starts (drivers on odd hours — set wide)</label>
          <input type="time" value={form.tracking_start || ''} onChange={set('tracking_start')}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tracking ends</label>
          <input type="time" value={form.tracking_end || ''} onChange={set('tracking_end')}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
      </div>
      <p className="text-xs text-gray-400 mt-2">
        An end time earlier than the start (e.g. 18:00 → 02:00) is treated as running overnight.
        Outside these hours the phone records nothing at all.
      </p>
      <button onClick={save} disabled={saving}
        className="mt-3 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
        {saving ? 'Saving…' : 'Save safety settings'}
      </button>
    </div>
  );
}

// A single on/off pill switch.
function Switch({ on, onClick, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${on ? 'bg-green-500' : 'bg-gray-300'}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

// Master switches: turn a whole notification channel on/off for EVERY client.
function MasterNotifyCard() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(null);   // which channel is mid-update
  const { data } = useQuery({
    queryKey: ['notify-summary'],
    queryFn: () => api.get('/clients/notify-summary').then((r) => r.data),
  });

  const total = data?.total || 0;
  const channels = [
    { key: 'email',    label: 'Email',    icon: Mail,          count: data?.email,
      desc: 'Auto-send an email to the client on every parcel status change', color: 'text-blue-500' },
    { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, count: data?.whatsapp,
      desc: 'Marks clients for the manual WhatsApp templates staff send', color: 'text-green-500' },
    { key: 'telegram', label: 'Telegram', icon: Send,          count: data?.telegram,
      desc: 'Post a status alert to your Telegram group on every parcel change', color: 'text-sky-500' },
  ];

  const flip = async (channel, enabled) => {
    if (enabled && !window.confirm(
      `Turn ${channel.toUpperCase()} notifications ON for all ${total} clients?\n\n` +
      (channel === 'email'
        ? 'From now on every client with an email address will be emailed automatically whenever one of their parcels changes status.'
        : channel === 'telegram'
        ? 'Every parcel status change will post a message to your Telegram group.'
        : 'All clients will be flagged for WhatsApp — staff still send those messages manually.')
    )) return;
    setBusy(channel);
    try {
      const r = await api.post('/clients/notify-bulk', { channel, enabled });
      toast.success(`${channel} turned ${enabled ? 'ON' : 'OFF'} for ${r.data.changed} client${r.data.changed === 1 ? '' : 's'}`);
      qc.invalidateQueries({ queryKey: ['notify-summary'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not update');
    } finally { setBusy(null); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
      <div>
        <h2 className="font-bold text-gray-800 flex items-center gap-2"><Bell size={17} className="text-orange-500" /> Client Notifications — Master Switch</h2>
        <p className="text-xs text-gray-400 mt-1">
          Turn a channel on or off for <strong>all {total} clients</strong> at once. Each switch shows how
          many clients currently have it on. You can still change individual clients on their own card.
        </p>
      </div>
      {channels.map((c) => {
        const allOn = total > 0 && c.count === total;
        const someOn = c.count > 0 && !allOn;
        return (
          <div key={c.key} className="flex items-center gap-3 border-t pt-4 first:border-t-0 first:pt-0">
            <c.icon size={20} className={c.color} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium text-gray-800 text-sm">{c.label}</p>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${allOn ? 'bg-green-100 text-green-700' : someOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                  {c.count ?? 0} of {total} on
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">{c.desc}</p>
            </div>
            <Switch on={allOn} disabled={busy === c.key} onClick={() => flip(c.key, !allOn)} />
          </div>
        );
      })}
      {channels.some((c) => c.count > 0 && c.count < total) && (
        <p className="text-[11px] text-amber-600 flex items-start gap-1 pt-1">
          <Info size={13} className="flex-shrink-0 mt-0.5" />
          A switch shows green only when <strong>every</strong> client has that channel on. "3 of 345 on" means
          some were set individually — flip the switch to make them all the same.
        </p>
      )}
    </div>
  );
}

function TabNotifications() {
  const [tg, setTg] = useState({ telegram_bot_token: '', telegram_chat_id: '', telegram_token_set: false });
  const [tgLoading, setTgLoading] = useState(false);
  const [tgTestLoading, setTgTestLoading] = useState(false);
  const [smtp, setSmtp] = useState({});
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [showChatIdHelp, setShowChatIdHelp] = useState(false);

  const { data: tgData } = useQuery({
    queryKey: ['telegram-settings'],
    queryFn: () => api.get('/settings/telegram').then((r) => r.data),
  });

  const { data: smtpData } = useQuery({
    queryKey: ['email-settings'],
    queryFn: () => api.get('/settings/email').then((r) => r.data),
  });

  useEffect(() => { if (tgData) setTg((t) => ({ ...t, ...tgData })); }, [tgData]);
  useEffect(() => { if (smtpData) setSmtp(smtpData); }, [smtpData]);

  const saveTelegram = async (e) => {
    e.preventDefault();
    setTgLoading(true);
    try {
      await api.put('/settings/telegram', {
        telegram_bot_token: tg.telegram_bot_token || undefined,
        telegram_chat_id: tg.telegram_chat_id,
      });
      toast.success('Telegram settings saved');
      setTg((t) => ({ ...t, telegram_bot_token: '', telegram_token_set: true }));
    } catch { toast.error('Failed to save Telegram settings'); }
    finally { setTgLoading(false); }
  };

  const testTelegram = async () => {
    setTgTestLoading(true);
    try {
      await api.post('/settings/telegram/test');
      toast.success('Test message sent! Check your Telegram.');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to send test message'); }
    finally { setTgTestLoading(false); }
  };

  const saveSmtp = async (e) => {
    e.preventDefault();
    setSmtpLoading(true);
    try { await api.put('/settings/email', smtp); toast.success('Email settings saved'); }
    catch { toast.error('Failed to save email settings'); }
    finally { setSmtpLoading(false); }
  };

  const sendTestEmail = async () => {
    if (!testEmail) return toast.error('Enter a test email address');
    setTestEmailLoading(true);
    try {
      const { data } = await api.post('/settings/email/test', { to: testEmail });
      if (data.saved_to_sent) toast.success(`Test email sent to ${testEmail} — copy saved in "${data.sent_folder}"`);
      else if (data.sent_skipped) toast.success(`Test email sent to ${testEmail}`);
      else toast.error(`Sent to ${testEmail}, but the copy to Sent Items failed: ${data.sent_error}`);
    }
    catch (err) { toast.error(err.response?.data?.error || 'Failed to send test email'); }
    finally { setTestEmailLoading(false); }
  };

  return (
    <div className="space-y-5">
      {/* Master switches to turn a channel on/off for all clients at once */}
      <MasterNotifyCard />

      {/* Driver safety: SOS alert channels + tracking hours */}
      <SafetyCard />

      {/* How it works */}
      <div className="bg-gray-900 text-white rounded-xl p-5 space-y-4">
        <h2 className="font-bold text-sm uppercase tracking-wide text-gray-300">How Notifications Work</h2>
        <div className="grid grid-cols-1 gap-3">
          <div className="flex gap-3 p-3 bg-gray-800 rounded-lg">
            <Mail size={18} className="text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-white">Email — Automatic</p>
              <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
                Sent automatically to the recipient every time a parcel scan event is recorded.
                Requires the client to have an email address <span className="text-orange-400 font-medium">and</span> "Email" ticked in their client profile.
                Configure SMTP below to enable this.
              </p>
            </div>
          </div>
          <div className="flex gap-3 p-3 bg-gray-800 rounded-lg">
            <Send size={18} className="text-sky-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-white">Telegram — Automatic</p>
              <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
                Sent automatically to your configured Telegram chat/group when a scan event is recorded,
                <span className="text-orange-400 font-medium"> only if</span> "Telegram" is ticked on the client's profile.
                Great for keeping your team updated. Configure the bot below.
              </p>
            </div>
          </div>
          <div className="flex gap-3 p-3 bg-gray-800 rounded-lg">
            <MessageCircle size={18} className="text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-white">WhatsApp — Manual</p>
              <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
                <span className="text-yellow-400 font-medium">Not automatic.</span> When "WhatsApp" is ticked on a client, it flags them as a WhatsApp contact.
                Staff click the <span className="text-green-400 font-medium">WhatsApp button</span> next to the client's phone number (on the Clients or Parcels page)
                to open WhatsApp with a pre-filled message. Edit your message templates under <span className="text-orange-400 font-medium">Settings → Templates</span>.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Telegram */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-2 mb-1">
          <Send size={16} className="text-blue-500" />
          <h2 className="font-semibold text-gray-700">Telegram Notifications</h2>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          Send parcel status updates and alerts to a Telegram chat or group.
        </p>
        <form onSubmit={saveTelegram} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Bot Token {tg.telegram_token_set && <span className="text-green-600 font-normal">(saved)</span>}
            </label>
            <input type="password"
              placeholder={tg.telegram_token_set ? 'Enter new token to replace' : 'e.g. 8686223173:AAGQx...'}
              value={tg.telegram_bot_token}
              onChange={(e) => setTg((t) => ({ ...t, telegram_bot_token: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 font-mono" />
            <p className="text-xs text-gray-400 mt-1">Get this from <span className="font-medium text-gray-600">@BotFather</span> on Telegram. Your bot: <span className="font-medium text-blue-600">@ExpresswayCourierBot</span></p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-gray-600">Chat ID</label>
              <button type="button" onClick={() => setShowChatIdHelp((v) => !v)}
                className="text-xs text-orange-500 hover:underline">
                {showChatIdHelp ? 'Hide help' : 'How do I get this?'}
              </button>
            </div>
            <input type="text" placeholder="e.g. -1001234567890 or 123456789"
              value={tg.telegram_chat_id}
              onChange={(e) => setTg((t) => ({ ...t, telegram_chat_id: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 font-mono" />
            {showChatIdHelp && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-800 space-y-1.5">
                <p className="font-semibold">To get your Chat ID:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Open Telegram and start a chat with <span className="font-medium">@ExpresswayCourierBot</span> — or add it to a group</li>
                  <li>Send any message (e.g. "hello")</li>
                  <li>Open this URL in your browser:<br />
                    <code className="bg-blue-100 px-1 rounded break-all">https://api.telegram.org/bot8686223173:AAGQxv5QGLxUeYDIjh_v-fyNlZRYTNbRBfQ/getUpdates</code>
                  </li>
                  <li>Find <code className="bg-blue-100 px-1 rounded">"chat":&#123;"id":</code> — that number is your Chat ID</li>
                  <li>For a group: add the bot to the group, send a message, use the negative Chat ID (e.g. <code className="bg-blue-100 px-1 rounded">-1001234567</code>)</li>
                </ol>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={tgLoading}
              className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              {tgLoading ? 'Saving...' : 'Save Telegram Settings'}
            </button>
            <button type="button" onClick={testTelegram} disabled={tgTestLoading || (!tg.telegram_token_set && !tg.telegram_bot_token)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              {tgTestLoading ? 'Sending...' : 'Send Test Message'}
            </button>
          </div>
        </form>
      </div>

      {/* Email / SMTP */}
      <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-2 mb-1">
            <Mail size={16} className="text-gray-500" />
            <h2 className="font-semibold text-gray-700">Email Notifications (SMTP)</h2>
          </div>
          <p className="text-xs text-gray-400 mb-4">Configure outgoing email for client delivery notifications.</p>
          <form onSubmit={saveSmtp} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">SMTP Host</label>
                <input type="text" placeholder="smtp.gmail.com" value={smtp.smtp_host || ''}
                  onChange={(e) => setSmtp((s) => ({ ...s, smtp_host: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">SMTP Port</label>
                <input type="number" placeholder="587" value={smtp.smtp_port || '587'}
                  onChange={(e) => setSmtp((s) => ({ ...s, smtp_port: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">SMTP Username</label>
                <input type="text" placeholder="your@email.com" value={smtp.smtp_user || ''}
                  onChange={(e) => setSmtp((s) => ({ ...s, smtp_user: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  SMTP Password {smtp.smtp_pass_set && <span className="text-green-600 font-normal">(saved)</span>}
                </label>
                <input type="password" placeholder={smtp.smtp_pass_set ? 'Enter to change' : 'Password'}
                  value={smtp.smtp_pass || ''} onChange={(e) => setSmtp((s) => ({ ...s, smtp_pass: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">From Name</label>
                <input type="text" placeholder="Expressway Courier" value={smtp.from_name || ''}
                  onChange={(e) => setSmtp((s) => ({ ...s, from_name: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">From Email</label>
                <input type="email" placeholder="noreply@expresswaycourier.co.za" value={smtp.from_email || ''}
                  onChange={(e) => setSmtp((s) => ({ ...s, from_email: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
            </div>
            <p className="text-xs text-gray-400">
              TLS is automatic — use port <span className="font-medium text-gray-600">587</span> for STARTTLS (recommended) or <span className="font-medium text-gray-600">465</span> for SSL.
            </p>

            {/* Sent Items: sending over SMTP never files a copy — an email program does that over IMAP, and so can we */}
            <div className="border rounded-lg p-3 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={String(smtp.imap_save_sent) === 'true'}
                  onChange={(e) => setSmtp((s) => ({ ...s, imap_save_sent: e.target.checked ? 'true' : 'false' }))} className="mt-0.5 accent-orange-500" />
                <div>
                  <p className="text-sm font-semibold text-gray-800">Save a copy of every email to the mailbox&apos;s Sent Items</p>
                  <p className="text-xs text-gray-500">Invoices, statements, receipts and notifications then show in the Sent folder of {smtp.from_email || 'the sending mailbox'} like mail sent from Outlook. Uses IMAP — same server and login as above unless you fill these in.</p>
                </div>
              </label>
              {String(smtp.imap_save_sent) === 'true' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">IMAP server <span className="text-gray-400">(blank = same as SMTP)</span></label>
                    <input type="text" placeholder={smtp.smtp_host || 'mail.example.co.za'} value={smtp.imap_host || ''}
                      onChange={(e) => setSmtp((s) => ({ ...s, imap_host: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">IMAP port <span className="text-gray-400">(993 SSL)</span></label>
                    <input type="number" placeholder="993" value={smtp.imap_port || ''}
                      onChange={(e) => setSmtp((s) => ({ ...s, imap_port: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">IMAP username <span className="text-gray-400">(blank = SMTP username)</span></label>
                    <input type="text" placeholder={smtp.smtp_user || ''} value={smtp.imap_user || ''}
                      onChange={(e) => setSmtp((s) => ({ ...s, imap_user: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">IMAP password <span className="text-gray-400">(blank = SMTP password)</span>{smtp.imap_pass_set && <span className="text-green-600"> (saved)</span>}</label>
                    <input type="password" placeholder={smtp.imap_pass_set ? 'Enter to change' : 'same as SMTP'} value={smtp.imap_pass || ''}
                      onChange={(e) => setSmtp((s) => ({ ...s, imap_pass: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Sent folder <span className="text-gray-400">(blank = found automatically)</span></label>
                    <input type="text" placeholder="e.g. Sent or INBOX.Sent" value={smtp.imap_sent_folder || ''}
                      onChange={(e) => setSmtp((s) => ({ ...s, imap_sent_folder: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>
                </div>
              )}
            </div>
            <button type="submit" disabled={smtpLoading}
              className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              {smtpLoading ? 'Saving...' : 'Save Email Settings'}
            </button>
          </form>
          <div className="mt-5 pt-4 border-t">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Send Test Email</p>
            <div className="flex gap-2 max-w-sm">
              <input type="email" placeholder="Enter your email to test..." value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              <button onClick={sendTestEmail} disabled={testEmailLoading}
                className="bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 whitespace-nowrap">
                {testEmailLoading ? 'Sending...' : 'Send Test'}
              </button>
            </div>
          </div>
        </div>
    </div>
  );
}

const EVENTS = [
  { key: 'collected',        label: 'Parcel Collected',   emoji: '📦' },
  { key: 'at_depot',         label: 'Arrived at Depot',   emoji: '🏪' },
  { key: 'out_for_delivery', label: 'Out for Delivery',   emoji: '🚚' },
  { key: 'delivered',        label: 'Delivered',          emoji: '✅' },
  { key: 'failed_delivery',  label: 'Failed Delivery',    emoji: '⚠️' },
];

const CHANNELS = [
  { key: 'email',     label: 'Email',     icon: Mail },
  { key: 'telegram',  label: 'Telegram',  icon: Send },
  { key: 'whatsapp',  label: 'WhatsApp',  icon: MessageCircle },
];

function TabTemplates() {
  const [channel, setChannel] = useState('email');
  const [templates, setTemplates] = useState({});
  const [saving, setSaving] = useState({});

  const { data: rows = [] } = useQuery({
    queryKey: ['notification-templates'],
    queryFn: () => api.get('/settings/templates').then((r) => r.data),
  });

  useEffect(() => {
    if (!rows.length) return;
    const map = {};
    rows.forEach((t) => {
      if (!map[t.channel]) map[t.channel] = {};
      map[t.channel][t.event_type] = { subject: t.subject || '', body: t.body || '' };
    });
    setTemplates(map);
  }, [rows]);

  const update = (eventKey, field, value) =>
    setTemplates((t) => ({
      ...t,
      [channel]: { ...t[channel], [eventKey]: { ...(t[channel]?.[eventKey] || {}), [field]: value } },
    }));

  const save = async (eventKey) => {
    setSaving((s) => ({ ...s, [eventKey]: true }));
    try {
      await api.put(`/settings/templates/${channel}/${eventKey}`, templates[channel]?.[eventKey] || {});
      toast.success('Template saved');
    } catch { toast.error('Failed to save template'); }
    finally { setSaving((s) => ({ ...s, [eventKey]: false })); }
  };

  return (
    <div className="space-y-4">
      {/* Channel selector */}
      <div className="flex gap-1 bg-white rounded-xl shadow-sm p-1.5">
        {CHANNELS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setChannel(key)}
            className={`flex items-center gap-2 flex-1 justify-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              channel === key ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
            }`}>
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {/* Variables hint */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 space-y-2">
        <p className="text-xs font-semibold text-blue-700">Available variables</p>
        <div className="grid grid-cols-2 gap-1">
          {[
            ['{name}','Recipient name'],['{tracking}','Tracking number'],['{trackUrl}','Tracking link'],
            ['{sender}','Sender name'],['{status}','Current status'],['{reason}','Failure reason'],
            ['{price}','Parcel price'],['{driver}','Driver name'],['{date}','Delivery date'],['{description}','Description'],
          ].map(([v,d]) => (
            <div key={v} className="flex items-center gap-1.5">
              <code className="text-xs bg-blue-100 text-blue-800 rounded px-1.5 py-0.5 font-mono">{v}</code>
              <span className="text-xs text-blue-500">{d}</span>
            </div>
          ))}
        </div>
        {channel === 'whatsapp' && (
          <p className="text-xs text-blue-500 italic">WhatsApp: open the parcel, pick a template, edit if needed, then send manually.</p>
        )}
        {channel === 'telegram' && (
          <p className="text-xs text-blue-500 italic">Telegram: sent automatically on every scan event. Supports *bold* and _italic_ Markdown.</p>
        )}
        {channel === 'email' && (
          <p className="text-xs text-blue-500 italic">Email: sent automatically when notify_email is enabled for the recipient.</p>
        )}
      </div>

      {/* Template cards */}
      {EVENTS.map(({ key, label, emoji }) => {
        const tpl = templates[channel]?.[key] || {};
        return (
          <div key={key} className="bg-white rounded-xl shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-base">{emoji}</span>
                <h3 className="font-semibold text-gray-800 text-sm">{label}</h3>
              </div>
              <button onClick={() => save(key)} disabled={saving[key]}
                className="text-xs bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-lg disabled:opacity-50 font-medium">
                {saving[key] ? 'Saving…' : 'Save'}
              </button>
            </div>
            {channel === 'email' && (
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">Subject</label>
                <input type="text" value={tpl.subject || ''}
                  onChange={(e) => update(key, 'subject', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {channel === 'email' ? 'Body' : 'Message'}
              </label>
              <textarea rows={channel === 'email' ? 5 : 3} value={tpl.body || ''}
                onChange={(e) => update(key, 'body', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 font-mono" />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Billing details (shown on tax invoices, credit notes and statements) ─────
function BillingSettingsCard() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);

  // The logo endpoint needs the login token, so an <img src> can't fetch it
  // directly — pull it as a blob and show that.
  const loadLogo = async () => {
    try {
      const { data } = await api.get('/settings/billing/logo', { responseType: 'blob' });
      setLogoUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(data); });
    } catch {
      setLogoUrl(null);
    }
  };

  useEffect(() => {
    api.get('/settings/billing').then(r => setForm(r.data)).catch(() => setForm({}));
    loadLogo();
    return () => setLogoUrl((old) => { if (old) URL.revokeObjectURL(old); return null; });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/settings/billing', form);
      queryClient.invalidateQueries({ queryKey: ['billing-settings'] });
      toast.success('Billing details saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/settings/billing/logo', fd);
      setForm(f => ({ ...f, logo_custom: data.custom, logo_version: data.version }));
      await loadLogo();
      queryClient.invalidateQueries({ queryKey: ['billing-settings'] });
      toast.success('Logo updated — it will print on the next invoice');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not upload that logo');
    } finally {
      setLogoBusy(false);
    }
  };

  const resetLogo = async () => {
    setLogoBusy(true);
    try {
      const { data } = await api.delete('/settings/billing/logo');
      setForm(f => ({ ...f, logo_custom: data.custom, logo_version: data.version }));
      await loadLogo();
      queryClient.invalidateQueries({ queryKey: ['billing-settings'] });
      toast.success('Back to the standard Expressway logo');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not reset the logo');
    } finally {
      setLogoBusy(false);
    }
  };

  if (!form) return null;
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const input = 'w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400';

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 mb-4">
      <h2 className="font-semibold text-gray-800">Billing details</h2>
      <p className="text-xs text-gray-400 mt-0.5 mb-4">
        Printed at the top of every tax invoice, credit note and statement — the company name, registration and
        VAT numbers, address and logo SARS expects on a tax invoice, plus the banking details clients pay into.
      </p>

      {/* Logo */}
      <div className="flex flex-wrap items-center gap-4 mb-5 p-3 border rounded-lg bg-gray-50">
        <div className="h-16 w-40 flex items-center justify-center bg-white border rounded">
          {logoUrl
            ? <img src={logoUrl} alt="Company logo" className="max-h-14 max-w-[9.5rem] object-contain" />
            : <span className="text-xs text-gray-400">No logo</span>}
        </div>
        <div className="flex-1 min-w-[12rem]">
          <div className="text-sm font-medium text-gray-700">Logo</div>
          <div className="text-xs text-gray-400 mt-0.5">
            {form.logo_custom ? 'Your uploaded logo.' : 'The standard Expressway Courier logo.'} PNG or JPEG, under 2 MB.
            A wide logo on a white or transparent background prints best.
          </div>
          <div className="flex items-center gap-3 mt-2">
            <label className={`text-xs font-medium px-3 py-1.5 rounded-lg border bg-white cursor-pointer hover:bg-gray-100 ${logoBusy ? 'opacity-50 pointer-events-none' : ''}`}>
              {logoBusy ? 'Working…' : 'Upload new logo'}
              <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={uploadLogo} disabled={logoBusy} />
            </label>
            {form.logo_custom && (
              <button type="button" onClick={resetLogo} disabled={logoBusy}
                className="text-xs text-gray-500 hover:text-gray-700 underline disabled:opacity-50">
                Use the standard logo
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          ['billing_company_name', 'Company name (as registered)'],
          ['billing_company_reg',  'Company registration no.'],
          ['billing_vat_number',   'VAT number'],
          ['invoice_prefix',       'Invoice prefix (e.g. INV)'],
          ['billing_phone',        'Telephone'],
          ['billing_email',        'Email for account queries'],
        ].map(([k, lbl]) => (
          <div key={k}>
            <label className="block text-xs font-medium text-gray-600 mb-1">{lbl}</label>
            <input type="text" value={form[k] || ''} onChange={set(k)} className={input} />
          </div>
        ))}
        <div className="sm:col-span-2 border rounded-lg p-3 bg-orange-50/40">
          <p className="text-xs font-semibold text-gray-700 mb-2">Parcels heavier than the price list</p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Price list ends at</label>
              <div className="flex items-center gap-1"><input type="number" step="1" min="1" value={form.billing_over_kg || ''} onChange={set('billing_over_kg')} className={`${input} w-24 text-right`} /><span className="text-xs text-gray-500">kg</span></div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Then, per kg over (incl. VAT)</label>
              <div className="flex items-center gap-1"><span className="text-xs text-gray-500">R</span><input type="number" step="0.01" min="0" value={form.billing_over_rate || ''} onChange={set('billing_over_rate')} className={`${input} w-24 text-right`} /></div>
            </div>
            <p className="text-xs text-gray-500 basis-full">
              The first {form.billing_over_kg || 100} kg are charged at the top tier of the price list; every started kg above that adds R{parseFloat(form.billing_over_rate || 0).toFixed(2)}.
              Example: 120 kg = top tier + 20 × R{parseFloat(form.billing_over_rate || 0).toFixed(2)}.
            </p>
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Physical address (one line per row)</label>
          <textarea rows={3} value={form.billing_company_address || ''} onChange={set('billing_company_address')}
            placeholder={'56 Barnard Street\nGansbaai, 7220'} className={input} />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Banking details (shown on invoices — your Capitec account)
          </label>
          <textarea rows={4} value={form.billing_bank_details || ''} onChange={set('billing_bank_details')}
            placeholder={'Capitec Business: 123456789 (branch 450105)\nReference: your invoice number'}
            className={`${input} font-mono`} />
        </div>
      </div>

      {/* Pastel export posting config — optional; the export works without it,
          leaving these columns blank for the bookkeeper to map in Pastel. */}
      <div className="border-t mt-5 pt-4">
        <h3 className="font-semibold text-gray-700 text-sm">Pastel export (optional)</h3>
        <p className="text-xs text-gray-400 mt-0.5 mb-3">
          Codes from your Sage Pastel setup, used by Reports → Pastel Export. Leave blank to fill or map them in Pastel instead.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            ['pastel_sales_account', 'Sales GL account'],
            ['pastel_vat_type',      'VAT tax type (e.g. 01)'],
            ['pastel_cashbook_card', 'Cashbook — card'],
            ['pastel_cashbook_cash', 'Cashbook — cash'],
            ['pastel_cashbook_eft',  'Cashbook — EFT'],
          ].map(([k, lbl]) => (
            <div key={k}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{lbl}</label>
              <input type="text" value={form[k] || ''} onChange={set(k)} className={input} />
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving}
        className="mt-4 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
        {saving ? 'Saving…' : 'Save billing details'}
      </button>
    </div>
  );
}

// ── Price list for extra invoice items (Fastway labels, packaging, …) ─────────
// Things sold besides deliveries. An invoice copies the name and price when an
// item is added, so editing the list never changes an old invoice.
// ── Driver Pay: price list per outsourced driver ─────────────────────────────
// A driver paid per parcel by weight: one rate up to the first bracket, then a
// fixed step for every further bracket (or part). Used by Money → Driver Pay.
const R2 = (v) => `R${parseFloat(v || 0).toFixed(2)}`;

function DriverRateCard({ d, onChanged }) {
  const fromD = () => ({ first_kg: d.first_kg, first_rate: d.first_rate, step_kg: d.step_kg, step_rate: d.step_rate,
    over_kg: d.over_kg ?? '', over_rate: d.over_rate ?? '' });
  const [f, setF] = useState(fromD);
  const [busy, setBusy] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setF(fromD()); }, [d.first_kg, d.first_rate, d.step_kg, d.step_rate, d.over_kg, d.over_rate]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const n = (v) => parseFloat(v) || 0;
  // live preview of the bracket table the numbers produce
  const preview = Array.from({ length: 10 }, (_, i) => {
    const lo = i === 0 ? 0 : n(f.first_kg) + (i - 1) * n(f.step_kg) + 1;
    const hi = n(f.first_kg) + i * n(f.step_kg);
    return { lo, hi, rate: n(f.first_rate) + i * n(f.step_rate) };
  });
  const dirty = ['first_kg', 'first_rate', 'step_kg', 'step_rate', 'over_kg', 'over_rate'].some((k) => String(f[k] ?? '') !== String(d[k] ?? ''));
  const overOn = f.over_kg !== '' && f.over_rate !== '';

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/driver-pay/rates/${d.id}`, f);
      toast.success(`Rates saved for ${d.name}`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${d.name} from Driver Pay? Past payments are kept; they just stop appearing on the page.`)) return;
    try { await api.delete(`/driver-pay/rates/${d.id}`); toast.success('Removed'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not remove'); }
  };
  const Field = ({ k, label, suffix }) => (
    <div>
      <label className="block text-[10px] font-semibold text-gray-400 uppercase mb-1">{label}</label>
      <div className="flex items-center gap-1">
        <input type="number" step="0.01" min="0" value={f[k]} onChange={set(k)}
          className="w-24 border rounded-lg px-2 py-1.5 text-sm font-semibold text-right focus:outline-none focus:ring-2 focus:ring-orange-400" />
        <span className="text-xs text-gray-400">{suffix}</span>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-bold text-gray-800">{d.name}</h3>
          <p className="text-xs text-gray-500">{[d.email, d.phone].filter(Boolean).join(' · ') || 'no contact details'}{d.active ? '' : ' · inactive login'}</p>
        </div>
        <button onClick={remove} className="text-gray-400 hover:text-red-600" title="Remove from Driver Pay"><Trash2 size={16} /></button>
      </div>

      <div className="flex flex-wrap gap-4 mt-4">
        <Field k="first_kg" label="First bracket up to" suffix="kg" />
        <Field k="first_rate" label="pays" suffix="R" />
        <Field k="step_kg" label="then every further" suffix="kg (or part)" />
        <Field k="step_rate" label="adds" suffix="R" />
      </div>
      <div className="flex flex-wrap gap-4 mt-3">
        <Field k="over_kg" label="Heavy parcels: above" suffix="kg" />
        <Field k="over_rate" label="pay per kg over that" suffix="R per kg (blank = keep stepping)" />
      </div>
      <p className="text-xs text-gray-500 mt-2">Rated on the chargeable weight: actual or L×W×H÷5000, whichever is more.</p>

      {/* The table the way the driver's quote shows it */}
      <div className="mt-4 overflow-x-auto">
        <table className="text-xs border border-gray-300">
          <thead>
            <tr>{preview.map((b) => <th key={b.hi} className="border border-gray-300 px-2 py-1 font-bold text-red-600 whitespace-nowrap">{b.lo}–{b.hi} kg</th>)}</tr>
          </thead>
          <tbody>
            <tr>{preview.map((b) => <td key={b.hi} className="border border-gray-300 px-2 py-1 text-center whitespace-nowrap">{R2(b.rate)}</td>)}</tr>
          </tbody>
        </table>
        <p className="text-[11px] text-gray-400 mt-1">
          {overOn
            ? `… brackets continue to ${n(f.over_kg)} kg; above that, the ${n(f.over_kg)} kg rate plus ${R2(f.over_rate)} for every kg over (e.g. ${n(f.over_kg) + 20} kg = ${R2(n(f.first_rate) + Math.max(0, Math.ceil((n(f.over_kg) - n(f.first_kg)) / (n(f.step_kg) || 10))) * n(f.step_rate) + 20 * n(f.over_rate))}).`
            : `… and +${R2(f.step_rate)} for every further ${n(f.step_kg)} kg above ${preview[9].hi} kg.`}
        </p>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <button onClick={save} disabled={busy || !dirty}
          className="bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-semibold">
          {busy ? 'Saving…' : 'Save rates'}
        </button>
        {dirty && <span className="text-xs text-orange-600">Unsaved changes</span>}
      </div>
    </div>
  );
}

function TabDriverPay() {
  const qc = useQueryClient();
  const { data: drivers = [], isLoading } = useQuery({
    queryKey: ['driver-pay-drivers'],
    queryFn: () => api.get('/driver-pay/drivers').then((r) => r.data),
  });
  const { data: candidates = [] } = useQuery({
    queryKey: ['driver-pay-candidates'],
    queryFn: () => api.get('/driver-pay/candidates').then((r) => r.data),
  });
  const [adding, setAdding] = useState('');
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['driver-pay-drivers'] });
    qc.invalidateQueries({ queryKey: ['driver-pay-candidates'] });
  };
  const add = async () => {
    if (!adding) return;
    try {
      await api.put(`/driver-pay/rates/${adding}`, { first_kg: 10, first_rate: 75, step_kg: 10, step_rate: 10 });
      toast.success('Driver added — adjust the rates below');
      setAdding('');
      refresh();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not add'); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h2 className="font-bold text-gray-800">Outsourced drivers</h2>
        <p className="text-sm text-gray-500 mt-1">
          Drivers listed here are paid per parcel by weight. Their parcels and statements are under
          Money → Driver Pay. Changing a rate affects unpaid parcels only; paid statements keep the amounts they were paid at.
        </p>
        <div className="flex items-end gap-2 mt-4">
          <div>
            <label className="block text-[10px] font-semibold text-gray-400 uppercase mb-1">Add a driver</label>
            <select value={adding} onChange={(e) => setAdding(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 min-w-[200px]">
              <option value="">Choose a driver…</option>
              {candidates.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <button onClick={add} disabled={!adding}
            className="bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-semibold">Add</button>
        </div>
      </div>
      {isLoading ? <p className="text-sm text-gray-400">Loading…</p>
        : drivers.length === 0 ? <p className="text-sm text-gray-400 px-1">No outsourced drivers yet.</p>
        : drivers.map((d) => <DriverRateCard key={d.id} d={d} onChanged={refresh} />)}
    </div>
  );
}

function ProductsCard() {
  const queryClient = useQueryClient();
  const { data: products = [] } = useQuery({
    queryKey: ['billing-products', 'all'],
    queryFn: () => api.get('/billing/products', { params: { all: true } }).then(r => r.data),
  });
  const [draft, setDraft] = useState({ name: '', unit_price: '' });
  const [edit, setEdit] = useState(null);   // { id, name, unit_price }
  const [busy, setBusy] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['billing-products'] });
  const input = 'border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400';
  const fail = (err, fallback) => toast.error(err.response?.data?.error || fallback);

  const add = async () => {
    setBusy(true);
    try {
      await api.post('/billing/products', { name: draft.name, unit_price: draft.unit_price });
      setDraft({ name: '', unit_price: '' });
      refresh();
      toast.success('Item added to the price list');
    } catch (err) { fail(err, 'Could not add that item'); }
    finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const p = products.find(x => x.id === edit.id);
      await api.put(`/billing/products/${edit.id}`, { name: edit.name, unit_price: edit.unit_price, active: p ? p.active : true });
      setEdit(null);
      refresh();
      toast.success('Item updated');
    } catch (err) { fail(err, 'Could not save that item'); }
    finally { setBusy(false); }
  };
  const toggle = async (p) => {
    try {
      await api.put(`/billing/products/${p.id}`, { name: p.name, unit_price: p.unit_price, active: !p.active });
      refresh();
    } catch (err) { fail(err, 'Could not update that item'); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from the price list? Invoices that already have it are not affected.`)) return;
    try {
      await api.delete(`/billing/products/${p.id}`);
      refresh();
      toast.success('Item removed');
    } catch (err) { fail(err, 'Could not remove that item'); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 mb-4">
      <h2 className="font-semibold text-gray-800">Extra items you sell</h2>
      <p className="text-xs text-gray-400 mt-0.5 mb-4">
        Fastway labels, packaging and anything else billed besides deliveries. These appear in the
        &ldquo;Add from price list&rdquo; menu when an invoice is created. Prices include VAT.
      </p>

      {products.length > 0 && (
        <div className="border rounded-lg divide-y mb-4">
          {products.map((p) => (
            <div key={p.id} className={`flex flex-wrap items-center gap-2 px-3 py-2 text-sm ${p.active ? '' : 'opacity-50'}`}>
              {edit?.id === p.id ? (
                <>
                  <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={`${input} flex-1 min-w-40`} />
                  <input type="number" min="0" step="0.01" value={edit.unit_price}
                    onChange={(e) => setEdit({ ...edit, unit_price: e.target.value })} className={`${input} w-28 text-right`} />
                  <button onClick={save} disabled={busy} className="text-xs font-semibold text-orange-600 hover:underline disabled:opacity-50">Save</button>
                  <button onClick={() => setEdit(null)} className="text-xs text-gray-500 hover:underline">Cancel</button>
                </>
              ) : (
                <>
                  <span className="flex-1 min-w-40 text-gray-800">{p.name}{!p.active && <span className="text-xs text-gray-400"> · hidden</span>}</span>
                  <span className="w-28 text-right font-semibold">R{parseFloat(p.unit_price).toFixed(2)}</span>
                  <button onClick={() => setEdit({ id: p.id, name: p.name, unit_price: parseFloat(p.unit_price).toFixed(2) })}
                    className="text-xs text-gray-500 hover:text-gray-800 hover:underline">Edit</button>
                  <button onClick={() => toggle(p)} className="text-xs text-gray-500 hover:text-gray-800 hover:underline">
                    {p.active ? 'Hide' : 'Show'}
                  </button>
                  <button onClick={() => remove(p)} className="text-gray-300 hover:text-red-500" title="Remove"><Trash2 size={14} /></button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-48">
          <label className="block text-xs font-medium text-gray-600 mb-1">Item name</label>
          <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="e.g. Fastway label — local" className={`${input} w-full`} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Price incl. VAT</label>
          <input type="number" min="0" step="0.01" value={draft.unit_price}
            onChange={(e) => setDraft({ ...draft, unit_price: e.target.value })} placeholder="0.00" className={`${input} w-32 text-right`} />
        </div>
        <button onClick={add} disabled={busy || !draft.name.trim() || draft.unit_price === ''}
          className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
          Add item
        </button>
      </div>
    </div>
  );
}

// ── Database (danger zone) ────────────────────────────────────────────────────
const CONFIRM_PHRASE = 'DELETE ALL PARCELS';

// Disaster-recovery backups: nightly by cron, or on demand from here.
function BackupsCard() {
  const [running, setRunning] = useState(false);
  const [last, setLast] = useState(null);
  const [info, setInfo] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/settings/backup');
      setInfo(data);
    } catch { /* card just shows empty */ }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    setLast(null);
    try {
      const { data } = await api.post('/settings/backup/run');
      setLast(data);
      toast.success(`Backup created — ${data.rows} rows from ${data.tables} tables`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Backup failed');
    } finally {
      setRunning(false);
    }
  };

  const kb = (b) => `${(b / 1024).toFixed(1)} KB`;

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h2 className="font-semibold text-gray-800">Backups</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Runs automatically every night at 02:15. Each backup holds a full database dump, a CSV of
            every table, and a rebuild file explaining the data — enough to restore or rebuild from scratch.
          </p>
        </div>
        <button onClick={runNow} disabled={running}
          className="flex items-center gap-2 bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 flex-shrink-0">
          {running ? 'Backing up…' : 'Back up now'}
        </button>
      </div>

      {last && (
        <div className="bg-green-50 border border-green-100 rounded-lg px-4 py-3 text-sm text-green-900 mb-3">
          <strong>{last.file}</strong> · {kb(last.size)} · {last.tables} tables · {last.rows} rows
          {last.redacted?.length > 0 && (
            <div className="text-xs text-green-700 mt-1">
              Secrets left out on purpose: {last.redacted.join(', ')} — re-enter them after a restore.
            </div>
          )}
          {last.drive_error && <div className="text-xs text-red-600 mt-1">Google Drive: {last.drive_error}</div>}
        </div>
      )}

      <div className={`rounded-lg px-4 py-2.5 text-xs mb-3 ${
        info?.drive_configured ? 'bg-blue-50 text-blue-800' : 'bg-amber-50 text-amber-800'
      }`}>
        {info?.drive_configured
          ? <>Google Drive is connected — backups are uploaded off-site.</>
          : <><strong>Google Drive is not connected yet.</strong> Backups are saved on the server only,
              so they&apos;d be lost with it. Add the service-account key to switch on off-site copies.</>}
      </div>

      {info?.backups?.length > 0 && (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Backup</th>
              <th className="px-3 py-2 text-right">Size</th>
              <th className="px-3 py-2 text-left">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {info.backups.slice(0, 8).map((b) => (
              <tr key={b.file}>
                <td className="px-3 py-2 font-mono text-xs text-gray-600">{b.file}</td>
                <td className="px-3 py-2 text-right text-gray-500">{kb(b.size)}</td>
                <td className="px-3 py-2 text-gray-500 text-xs">
                  {new Date(b.created_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TabDatabase() {
  return (
    <div className="space-y-4 max-w-2xl">
      <BackupsCard />

      {/* The pre-launch "delete my test data" tool lived here. Retired at
          go-live: it deleted everything with source='expressway', which was
          test data before 30 July 2026 and is the live business after it. The
          API refuses it outright now; this note is here so nobody goes looking
          for the button. Roll back with a database restore instead. */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 text-sm text-gray-600">
        <p className="font-semibold text-gray-700 mb-1">Test-data cleanup — retired</p>
        <p>
          The go-live cleanup tool was switched off once real trading began on 30 July 2026, because
          everything created since then is live data. If something ever needs rolling back, restore
          from a nightly backup rather than deleting.
        </p>
      </div>

    </div>
  );
}

const TABS = [
  { key: 'account',       label: 'Account',       icon: KeyRound },
  { key: 'users',         label: 'Users',          icon: Users,    perm: 'system.users' },
  { key: 'roles',         label: 'Roles',          icon: KeyRound, perm: 'system.users' },
  { key: 'pricing',       label: 'Pricing',        icon: DollarSign, perm: 'system.settings' },
  { key: 'driverpay',     label: 'Driver Pay',     icon: HandCoins,  perm: 'system.settings' },
  { key: 'templates',     label: 'Templates',      icon: FileText, perm: 'system.settings' },
  { key: 'notifications', label: 'Notif. Server',  icon: Bell,     perm: 'system.settings' },
  { key: 'server',        label: 'Server',         icon: Server,   perm: 'system.settings' },
  { key: 'database',      label: 'Database',       icon: Database, perm: 'system.settings' },
];

export default function Settings() {
  const availableTabs = TABS.filter(t => !t.perm || can(t.perm));
  // ?tab=database lets other pages link straight to a tab (e.g. the backup warning)
  const [searchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const [tab, setTab] = useState(
    availableTabs.some(t => t.key === requested) ? requested : availableTabs[0].key
  );

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <h1 className="text-xl font-bold text-gray-800">Settings</h1>

      {/* Tab bar */}
      <div className="flex gap-1 bg-white rounded-xl shadow-sm p-1.5">
        {availableTabs.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 flex-1 justify-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === key ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
            }`}>
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {tab === 'account' && <TabAccount />}
      {tab === 'users' && <TabUsers />}
      {tab === 'roles' && <TabRoles />}
      {tab === 'pricing' && <><BillingSettingsCard /><ProductsCard /><Pricing /></>}
      {tab === 'driverpay' && <TabDriverPay />}
      {tab === 'templates' && <TabTemplates />}
      {tab === 'notifications' && <TabNotifications />}
      {tab === 'server' && <TabServer />}
      {tab === 'database' && <TabDatabase />}
    </div>
  );
}
